import React, { Suspense } from 'react'
import { Route, Switch } from 'react-router-dom'

import { store } from '../redux/store'
import { isAdmin, isSuperAdmin } from 'helpers/isAdmin'

import PageLoading from '_components/PageLoading'

const Dashboard = React.lazy(() => import('_pages/Dashboard' /* webpackChunkName: "dashboard" */))
const Domains = React.lazy(() => import('_pages/Domains' /* webpackChunkName: "domains" */))
const ManageUsers = React.lazy(() =>
  import('_pages/Account/ManageUsers' /* webpackChunkName: "manage-users" */)
)
const ManageEmailNotifications = React.lazy(() =>
  import(
    '_pages/Account/ManageEmailNotifications' /* webpackChunkName: "manage-emailnotifications" */
  )
)
const UserDashboard = React.lazy(() =>
  import('_pages/Account/UserDashboard' /* webpackChunkName: "user-dashboard" */)
)
const KeywordFinder = React.lazy(() => import('_pages/KeywordFinder' /* webpackChunkName: "kf" */))
const BacklinkChecker = React.lazy(() => import('_pages/BacklinkChecker' /* webpackChunkName: "blc" */))
const Reports = React.lazy(() => import('_pages/Reports' /* webpackChunkName: "rep" */))
const DomainReports = React.lazy(() =>
  import('_pages/Reports/Domain' /* webpackChunkName: "rep-domain" */)
)
const ReportController = React.lazy(() =>
  import('_pages/Reports/Editor' /* webpackChunkName: "rep-editor" */)
)
const PreviousReportController = React.lazy(() =>
  import('_pages/Reports/Previous' /* webpackChunkName: "rep-prev" */)
)
const Overview = React.lazy(() =>
  import('_pages/Ranktracker/Overview' /* webpackChunkName: "rt-overview" */)
)
const Competitors = React.lazy(() =>
  import('_pages/Ranktracker/Competitors' /* webpackChunkName: "rt-competitors" */)
)
const Pages = React.lazy(() =>
  import('_pages/Ranktracker/Pages' /* webpackChunkName: "rt-pages" */)
)
const Grossing = React.lazy(() =>
  import('_pages/Ranktracker/Grossing' /* webpackChunkName: "rt-grossing" */)
)
const Metrics = React.lazy(() =>
  import('_pages/Ranktracker/Metrics' /* webpackChunkName: "rt-metrics" */)
)
const Discovery = React.lazy(() =>
  import('_pages/Ranktracker/Discovery' /* webpackChunkName: "rt-discovery" */)
)
const Savings = React.lazy(() =>
  import('_pages/Ranktracker/Savings' /* webpackChunkName: "rt-savings" */)
)
const Movements = React.lazy(() =>
  import('_pages/Ranktracker/Movements' /* webpackChunkName: "rt-movements" */)
)
const Grid = React.lazy(() =>
  import('_pages/Ranktracker/Grid' /* webpackChunkName: "rt-grid" */)
)
const KeywordDetails = React.lazy(() =>
  import('_pages/Ranktracker/KeywordDetails' /* webpackChunkName: "rt-keyworddetails" */)
)
const SeoChecklistDetails = React.lazy(() =>
  import('_pages/SeoChecklist/Details' /* webpackChunkName: "seo-checklist-details" */)
)
const SeoChecklist = React.lazy(() =>
  import('_pages/SeoChecklist' /* webpackChunkName: "seo-checklist" */)
)
const Wizard = React.lazy(() => import('_pages/Wizard' /* webpackChunkName: "wiz" */))
const SerpChecker = React.lazy(() => import('_pages/SerpChecker' /* webpackChunkName: "serp" */))
const WebsiteAudit = React.lazy(() => import('_pages/WebsiteAudit' /* webpackChunkName: "wa" */))
const WebsiteAuditReport = React.lazy(() =>
  import('_pages/WebsiteAudit/Report' /* webpackChunkName: "wa-rep" */)
)
const HowToRank = React.lazy(() => import('_pages/HowToRank' /* webpackChunkName: "seo-guide" */))
const CrawledPage = React.lazy(() =>
  import('_pages/WebsiteAudit/Crawled' /* webpackChunkName: "wa-crawl" */)
)

const CompareCrawls = React.lazy(() =>
  import('_pages/WebsiteAudit/Compare' /* webpackChunkName: "wa-comp" */)
)

const AdminUsers = React.lazy(() =>
  import('_pages/Admin/Users' /* webpackChunkName: "adm-users" */)
)
const AdminDomains = React.lazy(() =>
  import('_pages/Admin/Domains' /* webpackChunkName: "adm-domains" */)
)
const Plans = React.lazy(() => import('_pages/Plans' /* webpackChunkName: "plans" */))
const Addons = React.lazy(() => import('_pages/Addons' /* webpackChunkName: "addons" */))
const Lists = React.lazy(() => import('_pages/Lists' /* webpackChunkName: "lists" */))
const NotFound = React.lazy(() =>
  import('_pages/SitePages/NotFound' /* webpackChunkName: "not-found" */)
)
const NoAccess = React.lazy(() =>
  import('_pages/SitePages/NoAccess' /* webpackChunkName: "no-access" */)
)
const UpgradeModal = React.lazy(() =>
  import('_components/UpgradeModal' /* webpackChunkName: "upgrade" */)
)

const PrivateRoute = ({ component: Component, title = 'Ranktracker', ...rest }) => (
  <Route
    {...rest}
    render={props => {
      switch (true) {
        case isAdmin(store.getState().user):
        case isSuperAdmin(store.getState().user): {
          document.title = title
          return <Component {...props} />
        }
        default: {
          document.title = 'No access! - Ranktracker'
          return <NoAccess />
        }
      }
    }}
  />
)

const AdminRoute = ({ component: Component, title = 'Ranktracker', ...rest }) => (
  <Route
    {...rest}
    render={props => {
      switch (true) {
        case isSuperAdmin(store.getState().user): {
          document.title = title
          return <Component {...props} />
        }
        default: {
          document.title = 'No access! - Ranktracker'
          return <NoAccess />
        }
      }
    }}
  />
)

const LimitedAccessRoute = ({ render, feature, ...rest }) => {
  const disabledFeatures = store.getState().account.disabledFeatures || []
  const feautreNumber = disabledFeatures.indexOf(feature)
  return (
    <Route
      {...rest}
      render={props =>
        feautreNumber >= 0 ? (
          <UpgradeModal key={randomKey('upgrade-modal', 1, 1000)} selectedTab={feautreNumber} />
        ) : (
          render(props)
        )
      }
    />
  )
}

const randomKey = (id, min, max) => {
  return [id, Math.floor(Math.random() * (max - min + 1)) + min].join('')
}

const AppRoutes = () => {
  return (
    <Suspense fallback={<PageLoading />}>
      <Switch>
        <Route
          exact
          path="/dashboard"
          render={props => {
            document.title = 'Dashboard - Ranktracker'
            return <Dashboard {...props} />
          }}
        />
        <Route
          exact
          path="/domains"
          render={props => {
            document.title = 'Rank Tracker - Ranktracker'
            return <Domains {...props} />
          }}
        />
        <LimitedAccessRoute
          exact
          path="/keyword-finder"
          feature="keyword-finder"
          render={props => {
            document.title = 'Keyword Finder - Ranktracker'
            return <KeywordFinder {...props} key={randomKey('keyword-finder', 1, 1000)} />
          }}
        />
        <Route
          exact
          path="/keyword-finder/history/:historyId"
          render={props => {
            document.title = 'Keyword Finder - Ranktracker'
            return <KeywordFinder {...props} />
          }}
        />
        <LimitedAccessRoute
          exact
          path="/backlink-checker"
          feature="backlink-checker"
          render={props => {
            document.title = 'Backlink Checker - Ranktracker'
            return <BacklinkChecker {...props} key={randomKey('backlink-checker', 1, 1000)} />
          }}
        />
        <Route
          exact
          path="/backlink-checker/history/:historyId"
          render={props => {
            document.title = 'Backlink Checker - Ranktracker'
            return <BacklinkChecker {...props} />
          }}
        />
        <LimitedAccessRoute
          exact
          path="/serp-checker"
          feature="serp-checker"
          render={props => {
            document.title = 'SERP checker - Ranktracker'
            return <SerpChecker {...props} key={randomKey('serp-checker', 1, 1000)} />
          }}
        />
        <Route
          exact
          path="/serp-checker/history/:historyId"
          render={props => {
            document.title = 'SERP checker - Ranktracker'
            return <SerpChecker {...props} />
          }}
        />
        <Route
          exact
          path="/domains/add"
          render={props => {
            document.title = 'Add new domain - Ranktracker'
            return <Wizard {...props} />
          }}
        />
        <Route
          exact
          path="/domains/:id"
          render={props => {
            document.title = 'Rank Tracker - Ranktracker'
            return <Overview {...props} />
          }}
        />
        <Route
          exact
          path="/share/:id"
          render={props => {
            document.title = 'Share - Ranktracker'
            return <Overview {...props} />
          }}
        />
        <Route
          exact
          path="/domains/:id?/edit/:page"
          render={props => {
            document.title = 'Edit domain settings - Ranktracker'
            return (
              <Wizard
                id={props.match.params.id}
                page={parseInt(props.match.params.page, 0)}
                finishPage={parseInt(props.match.params.page, 0)}
                history={props.history}
              />
            )
          }}
        />
        <Route
          exact
          path="/seo-checklist/:domainId"
          render={props => {
            document.title = 'SEO Checklist - Ranktracker'
            return <SeoChecklistDetails {...props} />
          }}
        />
        <Route
          exact
          path="/seo-checklist"
          render={props => {
            document.title = 'SEO Checklist - Ranktracker'
            return <SeoChecklist {...props} />
          }}
        />
        <Route
          exact
          path="/domains/:id/competitors"
          render={props => {
            document.title = 'Competitors - Ranktracker'
            return <Competitors {...props} />
          }}
        />
        <Route
          exact
          path="/domains/:id/grossing"
          render={props => {
            document.title = 'Grossing - Ranktracker'
            return <Grossing {...props} />
          }}
        />
        <Route
          exact
          path="/domains/:id/metrics"
          render={props => {
            document.title = 'Metrics - Ranktracker'
            return <Metrics {...props} />
          }}
        />
        <Route
          exact
          path="/domains/:id/discovery"
          render={props => {
            document.title = 'Discover Keywords - Ranktracker'
            return <Discovery {...props} />
          }}
        />
        <Route
          exact
          path="/domains/:id/savings"
          render={props => {
            document.title = 'SEO / PPC Savings - Ranktracker'
            return <Savings {...props} />
          }}
        />
        <Route
          exact
          path="/domains/:id/movements"
          render={props => {
            document.title = 'Keywords Movements - Ranktracker'
            return <Movements {...props} />
          }}
        />
        <Route
          exact
          path="/domains/:id/grid"
          render={props => {
            document.title = 'Grid - Ranktracker'
            return <Grid {...props} />
          }}
        />
        <Route
          exact
          path="/domains/:id/pages"
          render={props => {
            document.title = 'Pages - Ranktracker'
            return <Pages {...props} />
          }}
        />
        <Route
          exact
          path="/domains/:id/details/:keywordId"
          render={props => {
            document.title = 'Keyword Details - Ranktracker'
            return <KeywordDetails {...props} />
          }}
        />
        <Route
          exact
          path="/reports"
          render={props => {
            document.title = 'Reporting - Ranktracker'
            return <Reports {...props} />
          }}
        />
        <Route
          exact
          path="/reports/:domainId"
          render={props => {
            document.title = 'Domain reports - Ranktracker'
            return <DomainReports {...props} />
          }}
        />

        <LimitedAccessRoute
          exact
          path="/website-audit"
          feature="website-audit"
          render={props => {
            document.title = 'Website Audit - Ranktracker'
            return <WebsiteAudit {...props} />
          }}
        />
        <LimitedAccessRoute
          exact
          path="/website-audit/:domainId"
          feature="website-audit"
          render={props => {
            document.title = 'Audit Report - Ranktracker'
            return <WebsiteAuditReport {...props} />
          }}
        />
        <LimitedAccessRoute
          exact
          path="/website-audit/:domainId/pages"
          feature="website-audit"
          render={props => {
            document.title = 'Crawled Pages - Ranktracker'
            return <CrawledPage {...props} />
          }}
        />
        <LimitedAccessRoute
          exact
          path="/website-audit/:domainId/compare"
          feature="website-audit"
          render={props => {
            document.title = 'Compare Crawls - Ranktracker'
            return <CompareCrawls {...props} />
          }}
        />
        <Route
          exact
          path="/reports/:domainId/create"
          render={props => {
            document.title = 'Create new report - Ranktracker'
            return <ReportController domainId={props.match.params.domainId} reportId={null} />
          }}
        />
        <Route
          exact
          path="/reports/:domainId?/report/:reportId"
          render={props => {
            document.title = 'Edit report - Ranktracker'
            return (
              <ReportController
                domainId={props.match.params.domainId}
                reportId={props.match.params.reportId}
              />
            )
          }}
        />
        <Route
          exact
          path="/reports/:domainId?/report/:reportId/previous"
          render={props => {
            document.title = 'Reports history - Ranktracker'
            return (
              <PreviousReportController
                domainId={props.match.params.domainId}
                reportId={props.match.params.reportId}
              />
            )
          }}
        />
        <Route
          exact
          path="/how-to-rank"
          render={_ => {
            document.title = 'Insane Rankings + Ranktracker'
            return <HowToRank />
          }}
        />
        <Route
          exact
          path="/plans"
          render={props => {
            document.title = 'Plans - Ranktracker'
            return <Plans {...props} />
          }}
        />
        <Route
          exact
          path="/addons"
          render={props => {
            document.title = 'Addons - Ranktracker'
            return <Addons {...props} />
          }}
        />
        <Route
          exact
          path="/account/"
          render={props => {
            document.title = 'Manage account settings - Ranktracker'
            return <UserDashboard {...props} />
          }}
        />
        <PrivateRoute
          exact
          path="/account/users"
          title="Manage users - Ranktracker"
          component={ManageUsers}
        />
        <PrivateRoute
          exact
          path="/account/email-notifications"
          title="Manage email notifications - Ranktracker"
          component={ManageEmailNotifications}
        />
        <Route
          exact
          path="/list/:id"
          render={props => {
            document.title = 'Ranktracker'
            return <Lists {...props} />
          }}
        />
        <AdminRoute exact path="/admin/users" title="Admin panel - Users" component={AdminUsers} />
        <AdminRoute
          exact
          path="/admin/:userId/domains"
          title="Admin panel - Users"
          component={AdminDomains}
        />
        <Route
          render={props => {
            document.title = 'Ranktracker'
            return <NotFound {...props} />
          }}
        />
      </Switch>
    </Suspense>
  )
}
export default AppRoutes
